import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Plus, X } from 'lucide-react';

const TestingForm = ({ data, onSave }) => {
  const [testData, setTestData] = useState({
    sat: data?.sat || null,
    act: data?.act || null,
    ap_exams: data?.ap_exams || []
  });

  const [hasSAT, setHasSAT] = useState(!!data?.sat);
  const [hasACT, setHasACT] = useState(!!data?.act);

  const handleSATChange = (field, value) => {
    setTestData({
      ...testData,
      sat: { ...testData.sat, [field]: value }
    });
  };

  const handleACTChange = (field, value) => {
    setTestData({
      ...testData,
      act: { ...testData.act, [field]: value }
    });
  };

  const addAPExam = () => {
    setTestData({
      ...testData,
      ap_exams: [...testData.ap_exams, { subject: '', score: '', year: '' }]
    });
  };

  const removeAPExam = (index) => {
    setTestData({
      ...testData,
      ap_exams: testData.ap_exams.filter((_, i) => i !== index)
    });
  };

  const handleAPChange = (index, field, value) => {
    const updated = [...testData.ap_exams];
    updated[index][field] = value;
    setTestData({ ...testData, ap_exams: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      sat: hasSAT ? testData.sat : null,
      act: hasACT ? testData.act : null,
      ap_exams: testData.ap_exams
    };
    onSave(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* SAT Section */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Checkbox
            id="hasSAT"
            checked={hasSAT}
            onCheckedChange={(checked) => {
              setHasSAT(checked);
              if (checked && !testData.sat) {
                setTestData({ ...testData, sat: { total: '', math: '', reading: '', date: '' } });
              }
            }}
          />
          <Label htmlFor="hasSAT" className="text-lg font-semibold cursor-pointer">
            I have taken the SAT
          </Label>
        </div>

        {hasSAT && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="satTotal">Total Score *</Label>
                <Input
                  id="satTotal"
                  type="number"
                  min="400"
                  max="1600"
                  value={testData.sat?.total || ''}
                  onChange={(e) => handleSATChange('total', e.target.value)}
                  required={hasSAT}
                  placeholder="400-1600"
                />
              </div>
              <div>
                <Label htmlFor="satMath">Math Score *</Label>
                <Input
                  id="satMath"
                  type="number"
                  min="200"
                  max="800"
                  value={testData.sat?.math || ''}
                  onChange={(e) => handleSATChange('math', e.target.value)}
                  required={hasSAT}
                  placeholder="200-800"
                />
              </div>
              <div>
                <Label htmlFor="satReading">Reading/Writing Score *</Label>
                <Input
                  id="satReading"
                  type="number"
                  min="200"
                  max="800"
                  value={testData.sat?.reading || ''}
                  onChange={(e) => handleSATChange('reading', e.target.value)}
                  required={hasSAT}
                  placeholder="200-800"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="satDate">Test Date *</Label>
              <Input
                id="satDate"
                type="month"
                value={testData.sat?.date || ''}
                onChange={(e) => handleSATChange('date', e.target.value)}
                required={hasSAT}
              />
            </div>
          </div>
        )}
      </Card>

      {/* ACT Section */}
      <Card className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Checkbox
            id="hasACT"
            checked={hasACT}
            onCheckedChange={(checked) => {
              setHasACT(checked);
              if (checked && !testData.act) {
                setTestData({ ...testData, act: { composite: '', english: '', math: '', reading: '', science: '', date: '' } });
              }
            }}
          />
          <Label htmlFor="hasACT" className="text-lg font-semibold cursor-pointer">
            I have taken the ACT
          </Label>
        </div>

        {hasACT && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="actComposite">Composite Score *</Label>
                <Input
                  id="actComposite"
                  type="number"
                  min="1"
                  max="36"
                  value={testData.act?.composite || ''}
                  onChange={(e) => handleACTChange('composite', e.target.value)}
                  required={hasACT}
                  placeholder="1-36"
                />
              </div>
              <div>
                <Label htmlFor="actEnglish">English *</Label>
                <Input
                  id="actEnglish"
                  type="number"
                  min="1"
                  max="36"
                  value={testData.act?.english || ''}
                  onChange={(e) => handleACTChange('english', e.target.value)}
                  required={hasACT}
                  placeholder="1-36"
                />
              </div>
              <div>
                <Label htmlFor="actMath">Math *</Label>
                <Input
                  id="actMath"
                  type="number"
                  min="1"
                  max="36"
                  value={testData.act?.math || ''}
                  onChange={(e) => handleACTChange('math', e.target.value)}
                  required={hasACT}
                  placeholder="1-36"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="actReading">Reading *</Label>
                <Input
                  id="actReading"
                  type="number"
                  min="1"
                  max="36"
                  value={testData.act?.reading || ''}
                  onChange={(e) => handleACTChange('reading', e.target.value)}
                  required={hasACT}
                  placeholder="1-36"
                />
              </div>
              <div>
                <Label htmlFor="actScience">Science *</Label>
                <Input
                  id="actScience"
                  type="number"
                  min="1"
                  max="36"
                  value={testData.act?.science || ''}
                  onChange={(e) => handleACTChange('science', e.target.value)}
                  required={hasACT}
                  placeholder="1-36"
                />
              </div>
              <div>
                <Label htmlFor="actDate">Test Date *</Label>
                <Input
                  id="actDate"
                  type="month"
                  value={testData.act?.date || ''}
                  onChange={(e) => handleACTChange('date', e.target.value)}
                  required={hasACT}
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* AP Exams Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">AP Exams (Optional)</h3>
        <div className="space-y-4">
          {testData.ap_exams.map((exam, index) => (
            <div key={index} className="flex gap-4 items-end">
              <div className="flex-1">
                <Label htmlFor={`apSubject-${index}`}>Subject</Label>
                <Input
                  id={`apSubject-${index}`}
                  value={exam.subject}
                  onChange={(e) => handleAPChange(index, 'subject', e.target.value)}
                  placeholder="e.g., AP Calculus AB"
                />
              </div>
              <div className="w-24">
                <Label htmlFor={`apScore-${index}`}>Score</Label>
                <Input
                  id={`apScore-${index}`}
                  type="number"
                  min="1"
                  max="5"
                  value={exam.score}
                  onChange={(e) => handleAPChange(index, 'score', e.target.value)}
                  placeholder="1-5"
                />
              </div>
              <div className="w-28">
                <Label htmlFor={`apYear-${index}`}>Year</Label>
                <Input
                  id={`apYear-${index}`}
                  type="number"
                  min="2000"
                  max="2030"
                  value={exam.year}
                  onChange={(e) => handleAPChange(index, 'year', e.target.value)}
                  placeholder="2024"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeAPExam(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addAPExam} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add AP Exam
          </Button>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit">Save & Continue</Button>
      </div>
    </form>
  );
};

export default TestingForm;